import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Create axios instance with JWT token
const api = axios.create({
  baseURL: API_URL,
});

// Add JWT token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * PDF Service - Handles all PDF-related API calls
 */
const pdfService = {
  /**
   * Generate HTML preview of certificate
   * @param {number} submissionId - Submission ID
   * @returns {Promise} - Response with HTML content
   */
  previewCertificate: async (submissionId) => {
    try {
      const response = await api.get(`/api/pdf/preview/${submissionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error generating preview' };
    }
  },

  /**
   * Generate PDF certificate
   * @param {number} submissionId - Submission ID
   * @returns {Promise} - Response with PDF info
   */
  generateCertificate: async (submissionId) => {
    try {
      const response = await api.post(`/api/pdf/generate/${submissionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error generating certificate' };
    }
  },

  /**
   * Download PDF certificate
   * @param {number} pdfId - PDF ID
   * @returns {Promise} - Blob data
   */
  downloadCertificate: async (pdfId) => {
    try {
      const response = await api.get(`/api/pdf/download/${pdfId}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      // When responseType is 'blob', errors are also returned as blobs
      // We need to convert the blob to JSON to read the error message
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw errorData;
        } catch (parseError) {
          throw { message: text || 'Error downloading certificate' };
        }
      }
      throw error.response?.data || { message: 'Error downloading certificate' };
    }
  },

  /**
   * Send certificate via email
   * @param {number} submissionId - Submission ID
   * @param {string} clientEmail - Client email address
   * @returns {Promise} - Response
   */
  sendCertificateEmail: async (submissionId, clientEmail) => {
    try {
      const response = await api.post(`/api/pdf/send-email/${submissionId}`, {
        client_email: clientEmail,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error sending certificate' };
    }
  },

  /**
   * Get list of generated PDFs for a submission
   * @param {number} submissionId - Submission ID
   * @returns {Promise} - List of PDFs
   */
  listCertificates: async (submissionId) => {
    try {
      const response = await api.get(`/api/pdf/list/${submissionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error fetching certificates' };
    }
  },

  /**
   * Download preview DOCX file
   * @param {string} filename - Preview filename
   * @returns {Promise} - Blob data
   */
  downloadPreview: async (filename) => {
    try {
      const response = await api.get(`/api/pdf/download-preview/${filename}`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      // When responseType is 'blob', errors are also returned as blobs
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw errorData;
        } catch (parseError) {
          throw { message: text || 'Error downloading preview' };
        }
      }
      throw error.response?.data || { message: 'Error downloading preview' };
    }
  },

  /**
   * Generate data-only preview (without seal and signatures)
   * @param {number} submissionId - Submission ID
   * @returns {Promise} - Response with DOCX filename
   */
  dataOnlyPreview: async (submissionId) => {
    try {
      const response = await api.get(`/api/pdf/data-only-preview/${submissionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { message: 'Error generating data-only preview' };
    }
  },
};

export default pdfService;
